import moment from "moment";
import { LookupData } from "../Models/LoookupData";
import { SelectOption } from "../shared/components/input";
import Logger from "../shared/services/Logger";
import { CommonDataDto } from "../customer/model/CommonDataDto";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Placement } from "react-bootstrap/esm/types";
import { KeyValuePair } from "../Models/KeyValuePair";
import { ValidationError } from "../Models/ValidationError";

export class CommonUtility {
    public static CS2DefaultDateFormat = 'MM/DD/YYYY';
    public static CS2DefaultDateTimeFormat = 'MM/DD/YYYY hh:mm A';
    public static DateTimeFormatWithSecond = 'MM/DD/YYYY hh:mm:ss A';
    public static APIDateOnlyFormat = 'YYYY-MM-DD';
    private static timeZoneOffSet: string = null;
    public static isEmailValid = (email: string): boolean => {
        return email
            ? /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i.test(email.trim())
            : false;
    }

    public static cloneObject = (objToClone: any) => {
        if (!objToClone) {
            return objToClone;
        }

        return { ...objToClone };
    }

    public static deepCloneObject = (objToClone: any) => {
        if (!objToClone) {
            return objToClone;
        }

        return JSON.parse(JSON.stringify(objToClone));
    }

    /// Use it while passing date only object to API - for api request models.
    public static datetimeToDateOnly = (date: Date) => {
        let dateOnly: any = moment(date).format(this.APIDateOnlyFormat);
        return dateOnly;
    }

    /// Use it if need to create date only object for local app. Javascript side. 
    public static datetimeToDateOnlyObject = (date: Date) => {
        if (!date) { return null; }
        return new Date(moment(date.toString()).format(this.CS2DefaultDateFormat));
    }

    public static mapToSelectOptions = (lookupData: LookupData[]): SelectOption[] => {
        return lookupData?.map(item => {
            return {
                label: item.lookupValue,
                value: item.id,
                isInactive: item.inactive
            };
        });
    }

    public static mapCommonDataDtoToSelectOptions = (lookupData: CommonDataDto[]): SelectOption[] => {
         return lookupData?.map(item => {
            return {
                label: item.displayName || item.description,
                value: item.id,
                isInactive: false,
                
            };
        });
    }

    public static truncateString = (inputString: string, length: number): string => {
        if (inputString.length > length) {
            return inputString.substring(0, length) + '...';
        } else {
            return inputString;
        }
    }

    public static getOffSet = () => {

        if (CommonUtility.timeZoneOffSet) {
            return CommonUtility.timeZoneOffSet;
        }

        const currentDate = new Date();
        const offsetInMinutes = currentDate.getTimezoneOffset();

        const sign = offsetInMinutes <= 0 ? "+" : "-";
        const absOffsetInMinutes = Math.abs(offsetInMinutes);
        const hours = Math.floor(absOffsetInMinutes / 60);
        const minutes = absOffsetInMinutes % 60;

        CommonUtility.timeZoneOffSet = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        return CommonUtility.timeZoneOffSet;
    }

    public static getLocalDateFromDateOnly = (date: any): Date => {

        if (typeof date === 'string') {
            let dateOnly = date;
            if (dateOnly.includes("T")) {
                dateOnly = dateOnly.substring(0, dateOnly.indexOf("T"));
            }

            return new Date(`${dateOnly}T00:00:00${CommonUtility.getOffSet()}`);
        }

        return date;
    }

    public static getDisplayDateString = (date: any): string => {

        if (typeof date === 'string') {
            let dateOnly = date;
            if (dateOnly.includes("T")) {
                dateOnly = dateOnly.substring(0, dateOnly.indexOf("T"));
            }
            else if (dateOnly.includes(" ")) {
                dateOnly = dateOnly.substring(0, dateOnly.indexOf(" "));
            }

            return dateOnly;
        }

        return date;
    }

    public static formateDate = (date: Date, dateFormat?: string): string => {
        if (!date) {
            return '';
        }

        let mnt = moment(date);
        let format = dateFormat ? dateFormat : CommonUtility.CS2DefaultDateFormat;
        return mnt.format(format);
    }

    public static formateDateTime = (date: Date, dateFormat?: string): string => {
        if (!date) {
            return '';
        }

        let mnt = moment(date);
        let format = dateFormat ? dateFormat : CommonUtility.CS2DefaultDateTimeFormat;
        return mnt.format(format);
    }

    public static getClipboardText = async (): Promise<string> => {
        try {
            return await navigator.clipboard.readText();
        }
        catch (ex) {
            Logger.logWarning("Error while reading clipboard data - ", ex);
        }

        return null;
    }

    public static isValidNumber = (str: string): boolean => {
        if (!str || str.trim().length == 0) {
            return false;
        }

        const num = Number(str);
        return !isNaN(num);
    }

    public static renderTooltip = (text: string): JSX.Element => (
        <Tooltip id={`tooltip-${text}`} placement='bottom'>
            {text}
        </Tooltip>
    );

    public static renderElementWithToolTip = (jsxElement: JSX.Element, toolTipText: string, elementKey: any, placement: Placement = 'top'): JSX.Element => {
        return <OverlayTrigger
            key={elementKey}
            placement={placement}
            overlay={CommonUtility.renderTooltip(toolTipText)}>
            {jsxElement}
        </OverlayTrigger>;
    }

    public static compareTimeDifferenceWithCurrent = (startDate, hours) => {
        try {
            if (hours !== 0 && startDate !== null) {
                // Parse the DateTimeOffset string into a JavaScript Date object
                const dateTime = new Date(startDate);
                // Convert the parsed date to the local time zone
                const localDateTime = new Date(dateTime.toLocaleString());
                // Get the current local date and time
                const currentDateTime = new Date();
                // Calculate the difference in milliseconds
                const differenceInMilliseconds = (currentDateTime as any) - (localDateTime as any);
                // Convert the difference to hours
                const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
                // Check if the difference is greater than 48 hours
                return differenceInHours > hours;
            }
        }
        catch (error) {
            console.log("compareTimeDifferenceWithCurrent " + error.toString());
        }

        return false;
    };

    public static zipCodeFormat = (value: string): any => {
        let val = value.toString().replace(/[^0-9-]/g, ''); // Remove non-digit and non-hyphen characters
        val = val.replace(/-/g, ''); // Remove existing hyphens

        if (value.toString().length > 5) {
            val = val.slice(0, 5) + '-' + val.slice(5); // Add hyphen after 5 characters
        }
        return val.slice(0, 10); // Limit to 10 characters including hyphen
    }

    public static validateZipCode = (zipCode: string, minLength: number = 5): { isValid: boolean, warningText?: string } => {
        const cleanedZip = zipCode ? zipCode.replace(/\D/g, '').trim() : '';
        
        if (!cleanedZip) {
            return { isValid: true };
        }
        if (cleanedZip.length < minLength) {
            return { isValid: false, warningText: "Zip code must be at least " + minLength + " digits." };
        } else if (cleanedZip.length > minLength && cleanedZip.length !== 9) {
            return { isValid: false, warningText: "Invalid Zip Code. Example 12345 or 12345-1234." };
        }
        return { isValid: true };
    }

    public static formatPolicy = (policyNumber, polEffDate, polExpDate): string => {
        if (policyNumber == null || policyNumber === '') {
            return '';
        }
        const effDateStr = CommonUtility.formateDate(polEffDate);
        const expDateStr = CommonUtility.formateDate(polExpDate);
        return `${policyNumber} : ${effDateStr} - ${expDateStr}`;
    }

        public static getPolicyDetailKey = (policyId: string, polEventId: string) => {
        return `${policyId}_${polEventId}`;
    }

    public static addToCurrentDate = (amount: number, unit: 'days' | 'months' | 'years'): Date => {
                if (!amount || !unit) {
                    return new Date(); // Return current date if no amount or unit is provided
                }

                const currentDate = moment(); // Get the current date using moment
                const updatedDate = currentDate.add(amount, unit); // Add the specified amount and unit
                return updatedDate.toDate(); // Convert back to JavaScript Date object
        };

    /**
    * Adds a specified amount of time to a given date.
    * @param date The base date to add to.
    * @param amount The amount to add.
    * @param unit The unit of time ('days', 'months', 'years').
    * @returns The new Date object.
    */
    public static addToDate(date: Date, amount: number, unit: 'days' | 'months' | 'years'): Date {
            if (!date || !amount || !unit) {
                return date;
            }
            const mnt = moment(date);
            return mnt.add(amount, unit).toDate();
        }
        
    public static capitalizeFirstCharOfEachWord = (input: string): string => {
        if (!input) return input;
        return input
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    public static escapeTime = (value:string): string => {
       if(value == null || value.trim().length == 0) {
        return value;
       }
        return value.replace('00:00:00', '');
    }

    /**
     * Normalizes the formatting of an email text by:
     * - Removing trailing whitespace from each line except the last.
     * - Trimming empty lines from the start and end of the text.
     * - Collapsing three or more consecutive newlines into two.
     *
     * @param text - The email text to normalize.
     * @returns The normalized email text.
     */
    public static normalizeEmailText = (text: string): string => {
        let lines = text.split('\n');
        if (lines.length > 1) {
            lines = lines.map((line, idx) =>
                idx === lines.length - 1 ? line : line.replace(/\s+$/, '')
            );
        }
        while (lines.length && lines[0].trim() === '') lines.shift();
        while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
        let normalized = lines.join('\n').replace(/\n{3,}/g, '\n\n');
        return normalized;
    };

    public static validateDecimal = (val: string): string => {
        if (val == null) return '';

        // Preserve single minus sign during typing
        if (val === '-') return val;

        // Remove any non-digit except dot and minus sign
        let v = val.replace(/[^0-9.-]/g, '');

        // Ensure minus sign is only at the start
        if (v.includes('-')) {
            v = v.startsWith('-') ? '-' + v.slice(1).replace(/-/g, '') : v.replace(/-/g, '');
        }

        // Keep only first dot
        const parts = v.split('.');
        if (parts.length > 1) {
            parts[0] = parts[0] || '0';
            parts[1] = parts.slice(1).join('');
            parts[1] = parts[1].slice(0, 2); // limit to 2 decimals
            v = parts[0] + '.' + parts[1];
        } else {
            v = parts[0];
        }

        // Remove leading zeros (but keep single zero and handle negative numbers)
        if (v.startsWith('-')) {
            const num = v.slice(1).replace(/^0+(\d)/, '$1');
            v = num ? '-' + num : '0';
        } else {
            v = v.replace(/^0+(\d)/, '$1');
        }

        return v;
    }

    public static formateDateStringtoDate = (date: string, dateFormat?: string): string => {
        if (!date) {
            return '';
        }

        let mnt = moment(date);
        let format = dateFormat ? dateFormat : CommonUtility.CS2DefaultDateFormat;
        return mnt.format(format);
    }

    public static validateDuplicates= (fields: KeyValuePair[], validationErrors: ValidationError, duplicateWarning: string) => {    
        const filteredFields = fields.filter(f => f.value);

        for (let firstIndex = 0; firstIndex < filteredFields.length; firstIndex++) {
            for (let secondIndex = firstIndex + 1; secondIndex < filteredFields.length; secondIndex++) {
                if (filteredFields[firstIndex].value === filteredFields[secondIndex].value) {
                    validationErrors[filteredFields[firstIndex].key] = { isValid: false, warningText: duplicateWarning };
                    validationErrors[filteredFields[secondIndex].key] = { isValid: false, warningText: duplicateWarning };
                }
            }
        }
    }

    public static isValidDecimalUpto2Limit = (value: string): boolean => {
        return value === '' || /^\d*\.?\d{0,2}$/.test(value);
    };

    public static isValidDecimalWithNegativeUpto2Limit = (value: string): boolean => {
        return value === '' || /^-?\d*\.?\d{0,2}$/.test(value);
    };

    // Partial input like "-" or "." returns 0 instead of NaN
    public static parseDecimalOrZero = (value: string): number => {
        const parsedValue = parseFloat(value);
        return Number.isNaN(parsedValue) ? 0 : parsedValue;
    };

    // Only strip T from date
    // e.g. "2025-03-31T12:19:00" → "2025-03-31 12:19:00"
    public static trimIsoTimeSuffix = (value: any): any => {
        if (typeof value !== 'string') {
            return value;
        }
        return value.replace('T', ' ');
    }
    /**
     * Validates if a year string matches the 4-digit YYYY format
     */
    public static isValidYear = (value: string): boolean => {
        return /^\d{4}$/.test(value);
    };

    /**
     * A renovation update counts as "selected" only if it holds a real dropdown
     * value ('P' or 'F'). Whitespace-only or legacy values are treated as unset,
     * keeping validation and the dropdown display in agreement.
     */
    public static isRenovationSelected = (value?: string): boolean => {
        const v = (value ?? '').trim();
        return v === 'P' || v === 'F';
    };
}